import * as Sentry from '@sentry/react';
import { getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../../design-system/button/button';
import { DataTableNew } from '../../../../design-system/data-table-new';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { EmployeeView, PayrollRecordView } from '../../../../types/entity-types';
import { PayrollRecordInput } from '../../hooks/use-payroll-record';
import { employeesTableColumnDefinition } from './employees-table-column-definition';

const PAGE_SIZE = 20;

type EmployeesTableProps = {
  employees: EmployeeView[];
  showHeader?: boolean;
  onEditEmployee: (employeeId: string) => void;
  onAddEmployee: () => void;
  onContinue?: () => void;
  selectedRowIds: string[];
  setSelectedRowIds: (ids: string[]) => void;
  payrollId?: string;
  addPayrollRecords?: (payrollId: string, records: PayrollRecordInput[]) => Promise<PayrollRecordView[]>;
  isLoading?: boolean;
};

export const EmployeesTable = ({
  employees,
  onEditEmployee,
  onAddEmployee,
  onContinue,
  selectedRowIds,
  setSelectedRowIds,
  payrollId,
  addPayrollRecords,
  isLoading = false,
}: EmployeesTableProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedRowIds.length > 0) return;

    setSelectedRowIds(employees.map((employee) => employee.id));
  }, []);

  const table = useReactTable({
    data: employees,
    columns: employeesTableColumnDefinition,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: PAGE_SIZE,
      },
    },
    meta: {
      onEditEmployee,
      selectedRowIds,
      setSelectedRowIds,
    },
  });

  const handleContinue = async () => {
    if (selectedRowIds.length === 0) {
      toast.error('Please select at least one employee to continue');
      return;
    }

    if (!payrollId || !addPayrollRecords) {
      onContinue?.();
      return;
    }

    setIsSubmitting(true);
    try {
      const records = selectedRowIds.map((employeeId) => ({
        employeeId,
      }));

      await addPayrollRecords(payrollId, records);
      onContinue?.();
    } catch (error) {
      toast.error('Failed to add employees to payroll');
      Sentry.captureException(error, {
        extra: {
          payrollId,
          employeesCount: selectedRowIds.length,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack space={6} align="stretch">
      <Inline space={2} className="mt-10 w-full justify-between">
        <h2 className="text-xl font-semibold">Employees</h2>
        <Button iconLeft={<User size={16} />} onClick={onAddEmployee}>
          Add Employee
        </Button>
      </Inline>
      <DataTableNew.Table table={table} aria-label="Employees table" />
      {table.getPageCount() > 1 && (
        <Inline justify="end">
          <DataTableNew.Pagination table={table} />
        </Inline>
      )}
      <Inline justify="end">
        <Button onClick={handleContinue} disabled={isSubmitting || isLoading} isLoading={isSubmitting || isLoading}>
          Continue
        </Button>
      </Inline>
    </Stack>
  );
};
